import type { CSSProperties } from "react";
import type { MotionStyle, MotionValue } from "framer-motion";
import { ColorType } from "@/theme";
import {
  CWITNESS_MOBILE_HERO_FLEX,
  CWITNESS_MOBILE_RIGHT_COLUMN_MASK,
  CWITNESS_OVERSCROLL_BEHAVIOR_AUTO,
  CWITNESS_TOUCH_ACTION_PAN_Y,
} from "./constants";
import type { ParallaxTextSpacingValue } from "./types";

interface CwitnessSectionStyleInput {
  style: CSSProperties | undefined;
  vh: number;
}

interface ResponsiveStyleInput {
  isMobile: boolean;
}

interface ViewportStyleInput {
  isMobile: boolean;
  vh: number;
}

interface HeroTextStyleInput {
  isCompact: boolean;
  isMobile: boolean;
}

interface ParallaxContentStyleInput {
  textBottomSpacing: ParallaxTextSpacingValue;
  textTopSpacing: ParallaxTextSpacingValue;
  textY: MotionValue<number>;
}

export function buildCwitnessSectionStyle({
  style,
  vh,
}: CwitnessSectionStyleInput): CSSProperties {
  return {
    position: "relative",
    backgroundColor: ColorType.Black,
    height: vh * 100,
    minHeight: vh * 100,
    maxHeight: vh * 100,
    overflow: "hidden",
    scrollSnapAlign: "start",
    scrollSnapStop: "always",
    ...style,
  };
}

export function buildRelativeContainerStyle({
  isMobile,
  vh,
}: ViewportStyleInput): CSSProperties {
  return {
    position: "relative",
    width: "100%",
    minHeight: "100%",
    height: vh * 100,
    margin: "0 auto",
    padding: isMobile ? "0 1rem" : "0 24px",
    ...(isMobile && {
      height: vh * 100,
    }),
  };
}

export function buildLeftColumnStyle({
  isMobile,
  vh,
}: ViewportStyleInput): CSSProperties {
  return isMobile
    ? {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "10vh 1.5rem",
        zIndex: 1,
      }
    : {
        position: "absolute",
        top: 0,
        left: 24,
        width: "39%",
        height: vh * 100,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: 44,
        padding: "96px 0 96px 2rem",
        zIndex: 1,
        alignItems: "flex-end",
      };
}

export function buildRightColumnStyle({
  isMobile,
  vh,
}: ViewportStyleInput): CSSProperties {
  const mobileRightColumnPaddingTop = vh * 30;

  return isMobile
    ? {
        position: "relative",
        width: "100%",
        height: vh * 100,
        display: "flex",
        flexDirection: "column",
        gap: "11rem",
        paddingTop: mobileRightColumnPaddingTop,
        paddingBottom: vh * 55,
        overflow: "auto",
        zIndex: 2,
        maskImage: CWITNESS_MOBILE_RIGHT_COLUMN_MASK,
        WebkitMaskImage: CWITNESS_MOBILE_RIGHT_COLUMN_MASK,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        WebkitOverflowScrolling: "touch",
        overscrollBehaviorY: CWITNESS_OVERSCROLL_BEHAVIOR_AUTO,
        touchAction: CWITNESS_TOUCH_ACTION_PAN_Y,
        willChange: "scroll-position",
      }
    : {
        position: "relative",
        width: "100%",
        height: vh * 100,
        overflow: "auto",
        paddingLeft: "44%",
        display: "flex",
        flexDirection: "column",
        gap: "10rem",
        paddingTop: vh * 30,
        paddingBottom: vh * 20,
        paddingRight: "1rem",
        zIndex: 2,
        scrollbarWidth: "none",
        msOverflowStyle: "none",
      };
}

export function buildHeroCardStyle({
  isMobile,
}: ResponsiveStyleInput): CSSProperties {
  return {
    flex: isMobile ? CWITNESS_MOBILE_HERO_FLEX : "0 0 auto",
    position: "relative",
  };
}

export function buildHeroContainerStyle({
  isMobile,
}: ResponsiveStyleInput): CSSProperties {
  return {
    ...buildHeroCardStyle({ isMobile }),
    padding: isMobile ? "1rem" : "2rem 2.5rem",
    ...(isMobile ? { borderRadius: "4rem" } : {}),
    boxShadow:
      "rgb(255 255 255 / 24%) 1px 1px 2px 0px inset, rgb(255 255 255 / 8%) -1px -2px 2px 0px inset, rgb(102 254 193 / 9%) 2px 3px 12px 0px",
  };
}

export function buildHeroGlowStyle({
  isMobile,
}: ResponsiveStyleInput): CSSProperties {
  return {
    position: "absolute",
    top: 0,
    right: 0,
    width: 300,
    height: 300,
    backgroundImage: "radial-gradient(circle, rgba(102, 254, 193, 0.21) 0%, rgba(10, 197, 178, 0.08) 70%)",
    filter: isMobile ? undefined : "blur(40px)",
    pointerEvents: "none",
    display: isMobile ? "none" : "flex",
    opacity: 0.5,
    transition: "opacity 0.3s ease",
  };
}

export function buildParallaxCardStyle({
  isMobile,
}: ResponsiveStyleInput): CSSProperties {
  return {
    position: "relative",
    height: isMobile ? 250 : 350,
    maskImage:
      "linear-gradient(transparent 0px, black 140px, transparent 400px)",
    ...(!isMobile && { maxWidth: "600px" }),
  };
}

export function buildHeroWrapperStyle({
  isMobile,
}: ResponsiveStyleInput): CSSProperties {
  return {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    ...(!isMobile && { maxWidth: "600px" }),
  };
}

export function buildHeroContentWrapperStyle({
  isMobile,
}: ResponsiveStyleInput): CSSProperties | undefined {
  return isMobile
    ? {
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
      }
    : undefined;
}

export function buildHeroTitleStyle({
  isCompact,
  isMobile,
}: HeroTextStyleInput): CSSProperties {
  return {
    zIndex: 1,
    ...(isMobile
      ? {
          letterSpacing: "3px",
          fontSize: "3rem",
          textAlign: "center",
        }
      : isCompact
        ? { fontSize: "3.4rem" }
        : {}),
  };
}

export function buildHeroSubtitleStyle({
  isCompact,
  isMobile,
}: HeroTextStyleInput): CSSProperties {
  return {
    ...(isMobile
      ? {
          letterSpacing: "3px",
          fontSize: "3rem",
          textAlign: "center",
        }
      : isCompact
        ? { letterSpacing: "4px", fontSize: "3rem" }
        : { letterSpacing: "4px" }),
  };
}

export function buildParallaxScrollableStyle({
  isMobile,
}: ResponsiveStyleInput): CSSProperties {
  return {
    height: "100%",
    position: "relative",
    paddingLeft: isMobile ? "1.5rem" : "2.5rem",
    paddingRight: isMobile ? "1.5rem" : "2.5rem",
    maskImage:
      "linear-gradient(transparent 0px, black 35%, #00000054 calc(50%), transparent 80%)",
    WebkitMaskImage:
      "linear-gradient(transparent 0px, black 35%, #00000054 calc(40%), transparent 80%)",
  };
}

export function buildParallaxContentStyle({
  textBottomSpacing,
  textTopSpacing,
  textY,
}: ParallaxContentStyleInput): MotionStyle {
  return {
    y: textY,
    display: "flex",
    flexDirection: "column",
    gap: 48,
    paddingTop: textTopSpacing,
    paddingBottom: textBottomSpacing,
  };
}
